import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import {
  DocumentHistoryItemCO,
  DocumentHistoryItemMX,
  RejectFormProps,
} from '@domains/documents-review/interfaces/interfaces';
import { getRejectReasonLabel } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/utils';
import {
  propertyDocumentTypeLabels,
  propertyTypeLabels,
} from './HistoryCardMX';
import { robotoRegular } from '@habitech/shared';
import { areaTypeLabels, lienLabels } from './HistoryCardCO';
import { formatAccessoryAreasForDisplay } from '@domains/documents-review/utils/accessoryAreas';
import { CountryCode } from '@domains/country';
import { formatPrice } from '@shared/utils';
import { BusinessType } from '@pages/DocumentAuditPage/modules/DocumentsAuditDashboard/components/DocumentsTable/components/BusinessType/BusinessType';
import styled from 'styled-components';

export type HistoryCardItem = DocumentHistoryItemCO | DocumentHistoryItemMX;

interface BaseProps {
  item: HistoryCardItem;
  index: number;
  labels: Record<string, string>;
  country: CountryCode;
}

const booleanFields = [
  'has_admin',
  'has_garage',
  'has_terrace',
  'has_garage_doc',
];

const StyleCard = styled(Card)`
  border-color: rgb(50, 0, 102) !important;
  font-size: 14px;
  font-family: ${robotoRegular};

  /* Badge de business_type — mismos colores que la tabla de tareas (BusinessType) */
  .badge-inmobiliaria {
    font-size: 13px;
    background-color: rgba(204, 229, 221, 1) !important;
    color: #206a5d !important;
  }
  .badge-comite {
    font-size: 13px;
    background-color: rgba(230, 239, 253, 1) !important;
    color: #1e3a8a !important;
  }
`;

export const formatValue = (
  value: unknown,
  key?: string,
  country: CountryCode = CountryCode.MX
) => {
  const labelMaps: Record<string, any> = {
    property_document_type: propertyDocumentTypeLabels,
    property_type: propertyTypeLabels,
    area_type: areaTypeLabels,
    liens: lienLabels,
  };

  if (key && key in labelMaps) {
    const labels = labelMaps[key];
    return labels?.[value as keyof typeof labels] ?? value;
  }

  if (key === 'admin_price' && typeof value === 'number') {
    return formatPrice(value);
  }

  if (key === 'accessory_areas' && typeof value === 'string') {
    return formatAccessoryAreasForDisplay(value, country);
  }

  if (value === 'Si' || value === 'No' || value === 'No aplica') {
    return value;
  }
  if (key && booleanFields.includes(key)) {
    const bool = value === true || value === 1;
    return bool ? 'Si' : 'No';
  }
  if (Array.isArray(value)) return value.join(', ');
  return value as string;
};

export const isRejectForm = (answer: unknown): answer is RejectFormProps => {
  return (
    !!answer &&
    typeof answer === 'object' &&
    'reject_reason' in answer &&
    (answer as any).reject_reason !== undefined
  );
};

export const HistoryCardBase = ({ item, index, labels, country }: BaseProps) => {
  const showRejectedInfo =
    (item.action === 'rejected' || item.answer_type === 'rejected') &&
    isRejectForm(item.answer_object);

  const showAnswerDetails =
    !showRejectedInfo && Object.keys(item.answer_object || {}).length > 0;

  const className = index === 0 ? 'border-primary border-2' : '';

  return (
    <StyleCard
      key={item.id}
      data-testid={`history-card-${index}`}
      className={className}
    >
      <Card.Body>
        <div className="d-flex justify-content-between fw-bold">
          <span>{item.date}</span>
          <span>{item.agent}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted">{item.task_type}</span>
          <BusinessType businessType={item.business_type} />
        </div>
        <div>{item.action}</div>

        {showRejectedInfo && isRejectForm(item.answer_object) && (
          <ListGroup className="pt-2">
            <ListGroup.Item>
              <strong>Motivo de rechazo:</strong>{' '}
              {getRejectReasonLabel(item.answer_object.reject_reason)}
            </ListGroup.Item>
            {item.answer_object.comment && (
              <ListGroup.Item>
                <strong>Comentario:</strong> {item.answer_object.comment}
              </ListGroup.Item>
            )}
          </ListGroup>
        )}

        {showAnswerDetails && (
          <ListGroup className="pt-2">
            {Object.entries(item.answer_object).map(([key, value]) => {
              if (!labels[key]) return null;
              if (key === 'accessory_areas' && !value) return null;
              return (
                <ListGroup.Item key={key}>
                  <strong>{labels[key]}:</strong> {formatValue(value, key, country)}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </Card.Body>
    </StyleCard>
  );
};
